import { useMemo, useState } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";

import { RoleTab } from "./RoleTab";
import { CompanyTab } from "./CompanyTab";

import { ApplicationProps, ApplicationView } from "./";

import { palette, pxToRem } from "@otta/design-tokens";
import { Button, Tabs } from "@otta/design";
import { pushAnalyticsEvent } from "@otta/analytics";
import { Loading } from "@otta/search/components/Loading";
import { NAV_HEIGHT } from "@otta/search/globalConstants";

const Wrapper = styled.div`
  margin: 0 auto;
`;

const SidebarTrigger = styled(Button).attrs({
  level: "secondary",
  size: "small",
})`
  height: ${pxToRem(32)};
  padding: 0 ${pxToRem(16)};
`;

const OpenSidebarButton = styled(SidebarTrigger)`
  position: absolute;
  right: ${pxToRem(20)};
  top: ${pxToRem(20)};
`;

const Sidebar = styled(motion.div).attrs({
  initial: { x: "100%" },
  animate: { x: 0 },
  exit: { x: "100%" },
  transition: { duration: 0.2 },
})`
  position: fixed;
  top: ${NAV_HEIGHT}px;
  right: 0;
  z-index: 1;
  width: ${pxToRem(582)};
  background-color: ${palette.grayscale.shade50};
  border-left: 1px solid ${palette.brand.grey};
`;

const TabsContent = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - ${NAV_HEIGHT}px);
  overflow-y: auto;
`;

const TabListWrapper = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${pxToRem(16)} ${pxToRem(24)};
  background-color: ${palette.grayscale.shade50};
`;

const TabPanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(24)};
  padding: ${pxToRem(8)} ${pxToRem(24)} ${pxToRem(24)};
  overflow-y: auto;
`;

export function DesktopApplication({
  job,
  jobId,
  analyticsData,
}: ApplicationProps) {
  const [showJobDetails, setShowJobDetails] = useState(false);

  const onOpenSidebar = () => {
    pushAnalyticsEvent({
      eventName: "Candidate Clicked",
      name: "show-job-details",
      page: "apply-via-otta-application",
      ...analyticsData,
    });
    setShowJobDetails(true);
  };

  const onCloseSidebar = () => {
    pushAnalyticsEvent({
      eventName: "Candidate Clicked",
      name: "close-job-details",
      page: "apply-via-otta-application",
      ...analyticsData,
    });
    setShowJobDetails(false);
  };

  const onClickJobTab = (tab: string) => {
    pushAnalyticsEvent({
      eventName: "Candidate Clicked",
      name: tab,
      page: "apply-via-otta-application",
      ...analyticsData,
    });
  };

  const tabs = useMemo(() => {
    return [
      {
        id: "role",
        title: "Role",
        content: job ? (
          <TabPanelWrapper>
            <RoleTab job={job} />
          </TabPanelWrapper>
        ) : (
          <Loading />
        ),
      },
      {
        id: "company",
        title: "Company",
        content: job ? (
          <TabPanelWrapper>
            <CompanyTab job={job} company={job.company} />
          </TabPanelWrapper>
        ) : (
          <Loading />
        ),
      },
    ];
  }, [job]);

  return (
    <Wrapper>
      <ApplicationView jobId={jobId} analyticsData={analyticsData} />
      <AnimatePresence>
        {showJobDetails && (
          <Sidebar>
            <Tabs tabs={tabs} onChange={onClickJobTab}>
              <TabsContent>
                <TabListWrapper>
                  <Tabs.List />
                  <SidebarTrigger onClick={onCloseSidebar}>
                    Close
                  </SidebarTrigger>
                </TabListWrapper>
                <Tabs.Panels />
              </TabsContent>
            </Tabs>
          </Sidebar>
        )}
      </AnimatePresence>
      <OpenSidebarButton onClick={onOpenSidebar}>
        Show job details
      </OpenSidebarButton>
    </Wrapper>
  );
}
